// ABOUTME: SPEC-53 — pure readers over the normalised product envelope the card receives inside
// ABOUTME: `stage_command.open_piece.product` (services/product_normalizer.py's shape).

import { WAITLIST_PRICE_LABEL } from '../Waitlist/waitlistGeometry';

/** The catalogue images, in order; the primary image alone when the record has no gallery. */
export function imagesOf(product) {
  const media = Array.isArray(product?.images) && product.images.length
    ? product.images
    : (Array.isArray(product?.media) ? product.media : []);
  const cleaned = media.filter((u) => typeof u === 'string' && u.trim());
  if (cleaned.length) return cleaned;
  const primary = product?.image_url || product?.image_uri;
  return primary ? [primary] : [];
}

// Review finding, 2026-09-10: checked EXPLICITLY on the flag, never inferred from the price string
// being empty — the same fix as CollectionOverlay.js/ReelProductCard.js/App.js's priceDisplay (R6:
// a stale/partially-normalized record could still carry a figure in the legacy `price_info` shim
// even once `waitlist` is stamped).
/** The piece's own total price as the envelope displays it (never computed here). */
export function priceOf(product) {
  return product?.waitlist ? WAITLIST_PRICE_LABEL : (product?.price?.display || product?.price_info?.price || '');
}

const cleanRows = (rows) => (Array.isArray(rows) ? rows : [])
  .filter((r) => r && r.k != null && r.v != null && String(r.v).trim() !== '')
  .map((r) => ({ k: String(r.k), v: Array.isArray(r.v) ? r.v.join(', ') : String(r.v) }));

/**
 * The card's Specification column as the grid renders it: the envelope's `attributeGroups` AS THEY
 * COME — group order, group labels, rows — and nothing else. The tenant's `attribute_groups` config
 * (product_normalizer.py) decides what a luxury card shows; the raw `specifications` map is the
 * catalogue's whole taxonomy (merchandising fields included) and is read ONLY when the envelope
 * carries no groups at all (a legacy record), as one unlabelled group.
 *
 * @returns {{ label: string, rows: {k: string, v: string}[] }[]}
 */
export function specGroups(product) {
  const groups = (Array.isArray(product?.attributeGroups) ? product.attributeGroups : [])
    .map((g) => ({ label: g?.label ? String(g.label) : '', rows: cleanRows(g?.rows) }))
    .filter((g) => g.rows.length);
  if (groups.length) return groups;
  const specs = product?.specifications && typeof product.specifications === 'object' ? product.specifications : {};
  const legacy = cleanRows(Object.entries(specs).map(([k, v]) => ({ k, v })));
  return legacy.length ? [{ label: '', rows: legacy }] : [];
}

/** `specGroups` flattened, in order — for readers that want rows regardless of grouping. */
export function specRows(product) {
  return specGroups(product).flatMap((g) => g.rows);
}

const humanize = (key) => String(key).replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * The card's variant subtitle — dial colour and hands, "Silver - Blue Markers" style (Figma
 * 403:1452). Read from the spec rows by key SHAPE, not by brand vocabulary: the first row whose key
 * names the dial (and is not a finish/pattern/size), then the first whose key names the hands or
 * markers. Falls back to the envelope's own `subtitle`.
 */
export function variantSubtitle(product) {
  const rows = specRows(product);
  const find = (test) => rows.find((r) => test(r.k.toLowerCase()))?.v;
  const dial = find((k) => /dial/.test(k) && !/(finish|pattern|size|diameter|material)/.test(k))
    || find((k) => /^(colou?r|dial)$/.test(k));
  const hands = find((k) => /hand/.test(k)) || find((k) => /(marker|index|numeral)/.test(k));
  const parts = [dial, hands].filter(Boolean).map(String);
  if (parts.length) return parts.join(' - ');
  return product?.subtitle || '';
}

/** A spec key as a label: `Power_Reserve` → "Power Reserve". Already-human keys pass through. */
export function specLabel(key) {
  return humanize(key);
}

/**
 * A short name for the reel collection, derived from the rail title the backend already sends
 * ("OPH 411 Vesper" → "Vesper") so the card's button can say "Back to Vesper" without any brand
 * vocabulary living in this code. Leading tokens that are pure reference codes — all digits, or
 * short all-caps house prefixes — are dropped; a title with no such prefix is returned whole.
 */
export function reelShortName(title) {
  const words = String(title || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '';
  let i = 0;
  while (i < words.length - 1 && (/^\d+$/.test(words[i]) || /^[A-Z0-9]{1,4}$/.test(words[i]))) i += 1;
  return words.slice(i).join(' ');
}
